import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Modal,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import axios from 'axios';
import { APIURL } from '../../../config';

const LinkAdl = (props) => {
    const [taskCategory, setTaskCategory] = useState('');
    const [search, setSearch] = useState('');
    const [activities, setActivities] = useState(props.activities || []);
    const [loading, setLoading] = useState(!props.activities);
    const [error, setError] = useState('');
    const [retry, setRetry] = useState(0);
    const [pinnedSelectedIds, setPinnedSelectedIds] = useState([]);
    const taskTypeId = String(props.taskType?.id);
    const selectedByCategory = props.selectedAdls[taskTypeId] || { ADL: [], IADL: [] };
    const selectedAll = [...new Set([...(selectedByCategory.ADL || []), ...(selectedByCategory.IADL || [])])];

    useEffect(() => {
        if (props.open) {
            setSearch('');
            setTaskCategory('');
            setPinnedSelectedIds([...new Set(selectedAll.map(String))]);
        }
    }, [props.open, props.taskType?.id]);

    useEffect(() => {
        if (props.activities) {
            setActivities(props.activities);
            setLoading(false);
            setError('');
            return undefined;
        }
        if (!props.open) return undefined;
        let active = true;
        setLoading(true);
        setError('');
        axios.get(`${APIURL}/adl_tasktype/catalog`)
            .then(({ data }) => {
                if (!Array.isArray(data)) throw new Error('Unexpected activity catalog response');
                if (active) setActivities(data);
            })
            .catch(requestError => {
                if (active) setError(requestError.response?.data?.message || 'Unable to load activities. Retry to try again.');
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => { active = false; };
    }, [props.activities, props.open, retry]);

    const taskCategories = useMemo(
        () => [...new Set(activities.map(activity => activity.task_category).filter(Boolean))].sort(),
        [activities]
    );
    const matchingActivities = useMemo(() => {
        const normalizedSearch = search.trim().toLocaleLowerCase();
        const pinnedSet = new Set(pinnedSelectedIds.map(String));
        const orderMap = new Map(activities.map((activity, index) => [String(activity.activity_id), index]));

        return activities
            .filter(activity =>
                (!taskCategory || activity.task_category === taskCategory) &&
                (!normalizedSearch || activity.activity_name.toLocaleLowerCase().includes(normalizedSearch))
            )
            .sort((a, b) => {
                const aSelected = pinnedSet.has(String(a.activity_id)) ? 1 : 0;
                const bSelected = pinnedSet.has(String(b.activity_id)) ? 1 : 0;
                if (aSelected !== bSelected) return bSelected - aSelected;
                return (orderMap.get(String(a.activity_id)) ?? 0) - (orderMap.get(String(b.activity_id)) ?? 0);
            });
    }, [activities, search, taskCategory, pinnedSelectedIds]);
    const visibleActivities = matchingActivities.slice(0, 100);

    const handleClose = () => props.setOpen(false);
    const handleCheckboxChange = (event, activityId) => {
        const checked = event.target.checked;
        const id = String(activityId);
        props.setSelectedAdls(previous => {
            const taskLinks = previous[taskTypeId] || { ADL: [], IADL: [] };
            const current = [...(taskLinks.ADL || []), ...(taskLinks.IADL || [])];
            const currentSet = new Set(current.map(item => String(item)));
            const nextSet = new Set(currentSet);
            if (checked) nextSet.add(id); else nextSet.delete(id);
            return {
                ...previous,
                [taskTypeId]: {
                    ADL: [...nextSet],
                    IADL: [],
                },
            };
        });
    };

    return (
        <Modal
            enforceFocus={false}
            open={props.open}
            onClose={handleClose}
            aria-labelledby="link-adl-modal"
            aria-describedby="link-adl-modal-description"
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: 'calc(100% - 32px)', sm: 560 },
                    height: { xs: 'calc(100vh - 64px)', sm: 620 },
                    boxSizing: 'border-box',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <Typography variant="h5" id="link-adl-modal" sx={{ mb: 1 }}>
                   Link ADL / IADL
                </Typography>
                <Typography variant="body1" id="link-adl-modal-description" sx={{ mb: 2 }}>
                   Activities for task type - {props.taskType?.title}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                   <TextField
                       fullWidth
                       size="small"
                       label="Search task name"
                       value={search}
                       onChange={event => setSearch(event.target.value)}
                       inputProps={{ 'aria-label': 'Search task name' }}
                   />
                   <FormControl fullWidth size="small">
                       <InputLabel id="link-adl-source-category-label">Task category</InputLabel>
                       <Select
                           labelId="link-adl-source-category-label"
                           value={taskCategory}
                           label="Task category"
                           onChange={event => setTaskCategory(event.target.value)}
                       >
                           <MenuItem value="">All categories</MenuItem>
                           {taskCategories.map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}
                       </Select>
                   </FormControl>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
                   Selected: {selectedAll.length}
                </Typography>
                {error ? (
                   <Alert
                       severity="error"
                       action={<Button color="inherit" size="small" onClick={() => setRetry(value => value + 1)}>Retry</Button>}
                   >
                       {error}
                   </Alert>
                ) : loading ? (
                   <Typography role="status">Loading activities...</Typography>
                ) : visibleActivities.length ? (
                   <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minHeight: 220, maxHeight: 420, overflowY: 'auto' }}>
                       {visibleActivities.map(activity => {
                           const isSelected = selectedAll.includes(String(activity.activity_id));
                           return (
                               <FormControlLabel
                                   key={activity.activity_id}
                                   sx={{
                                       m: 0,
                                       p: 1,
                                       borderRadius: 1,
                                       bgcolor: 'transparent',
                                       border: '1px solid transparent',
                                   }}
                                   control={
                                       <Checkbox
                                           checked={isSelected}
                                           onChange={event => handleCheckboxChange(event, activity.activity_id)}
                                           value={activity.activity_id}
                                           color="primary"
                                           data-activity-category={activity.task_category || 'Uncategorized'}
                                       />
                                   }
                                   label={
                                       <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, width: '100%' }}>
                                           <Box>
                                               <Typography variant="body2" sx={{ fontWeight: 400 }}>
                                                   {activity.activity_name}
                                               </Typography>
                                               <Typography variant="caption" color="text.secondary">
                                                   {activity.task_category || 'Uncategorized'}
                                               </Typography>
                                           </Box>
                                       </Box>
                                   }
                               />
                           );
                       })}
                       {matchingActivities.length > visibleActivities.length && (
                           <Typography color="text.secondary" sx={{ py: 1 }}>
                               Showing the first 100 matches. Refine your search to find more activities.
                           </Typography>
                       )}
                   </Box>
                ) : (
                   <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 180 }}>
                       <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                           No matches found.
                       </Typography>
                   </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, flexShrink: 0 }}>
                   <Button variant="contained" onClick={handleClose} disabled={loading || !!error}>
                       Done
                   </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default LinkAdl;
